<template>
  <div class="container">
    <div class="header-title">辟谣线索提供入口</div>

    <div class="form-wrapper">
      <el-form ref="ruleForm" :model="ruleForm" :rules="rules" label-width="140px" label-position="right" class="demo-ruleForm" hide-required-asterisk>

        <!-- 姓名 -->
        <el-form-item label="姓名" prop="name">
          <el-input v-model.trim="ruleForm.name" placeholder="请输入您的真实姓名" maxlength="50" />
        </el-form-item>

        <!-- 提供者类型 -->
        <el-form-item label="提供者类型">
          <el-radio-group v-model="providerType">
            <el-radio label="个人">个人</el-radio>
            <el-radio label="单位">单位</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 单位名称 -->
        <el-form-item v-if="providerType === '单位'" label="单位名称" prop="organization">
          <el-input v-model.trim="ruleForm.organization" placeholder="请输入单位全称" maxlength="100" />
        </el-form-item>

        <!-- 手机号 -->
        <el-form-item label="手机号" prop="phone">
          <el-input v-model.trim="ruleForm.phone" placeholder="请输入手机号" maxlength="11" />
        </el-form-item>

        <!-- 线索内容 -->
        <el-form-item label="线索内容" prop="content">
          <el-input
            v-model.trim="ruleForm.content"
            type="textarea"
            placeholder="请详细描述辟谣线索内容..."
            maxlength="1000"
            :autosize="{ minRows: 5 }"
          />
        </el-form-item>

        <!-- 媒体上传 -->
        <el-form-item prop="media" class="upload-item-block">
          <template slot="label">
            证据上传<br/>(图/视)
          </template>
          <el-upload
            ref="upload"
            :class="['upload-box', { 'hide-upload': isUploadLimit }]"
            list-type="picture-card"
            :file-list="fileList"
            :limit="6"
            action=""
            :before-upload="handleBeforeUpload"
            :http-request="customUpload"
            accept="image/*,video/*"
            :on-preview="handlePreview"
            :on-remove="handleRemove"
            :on-exceed="handleExceed"
          >
            <i class="el-icon-plus" />
            <!-- 自定义缩略图内容 -->
            <div slot="file" slot-scope="{file}">
              <template v-if="file.url">
                <video
                  v-if="isVideoFile(file)"
                  class="el-upload-list__item-thumbnail"
                  style="object-fit: cover; width: 100%; height: 100%;"
                >
                  <source :src="file.url" type="video/mp4">
                </video>
                <img
                  v-else
                  class="el-upload-list__item-thumbnail"
                  :src="file.url"
                >
              </template>

              <!-- 悬浮操作层 -->
              <span class="el-upload-list__item-actions">
                <!-- 预览 -->
                <span class="el-upload-list__item-preview" @click="handlePreview(file)">
                  <i class="el-icon-zoom-in"></i>
                </span>
                <!-- 删除：传入 file 对象 -->
                <span class="el-upload-list__item-delete" @click="onDelete(file)">
                  <i class="el-icon-delete"></i>
                </span>
               </span>
            </div>
            <div slot="tip" class="el-upload__tip">支持图片或视频，视频请上传1个，图片最多5张</div>
          </el-upload>
        </el-form-item>

        <!-- 提交按钮 -->
        <div class="submit-btn-wrapper">
          <el-button type="danger" class="publish-btn" :loading="submitting" @click="submitForm('ruleForm')">
            提交辟谣线索
          </el-button>
        </div>
      </el-form>
    </div>

    <!-- 预览 -->
    <el-dialog :visible.sync="dialogVisible" append-to-body width="50%">
      <img v-if="previewType === 'image'" width="100%" :src="dialogImageUrl">
      <video v-else width="100%" :src="dialogImageUrl" controls autoplay></video>
    </el-dialog>
  </div>
</template>

<script>
import { getPublicKey, reportInfoSave } from '@/api/publish-piyao'
import { JSEncrypt } from 'jsencrypt'
import { uploadFile } from "@/utils/upload";

export default {
  data() {
    return {
      submitting: false,
      providerType: '个人',
      ruleForm: {
        name: '',
        phone: '',
        organization: '',
        content: '',
        videoUrl: '',
        images: ''
      },
      rules: {
        name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '格式不正确', trigger: 'blur' }
        ],
        content: [{ required: true, message: '请输入内容', trigger: 'blur' }]
      },
      fileList: [],
      dialogVisible: false,
      dialogImageUrl: '',
      previewType: 'image'
    };
  },
  methods: {
    getMediaCount() {
      const uploadFiles = (this.$refs.upload && Array.isArray(this.$refs.upload.uploadFiles))
        ? this.$refs.upload.uploadFiles
        : this.fileList;
      const list = uploadFiles;
      const videoCount = list.filter(item => this.isVideoFile(item)).length;
      const imageCount = list.length - videoCount;
      return { videoCount, imageCount };
    },
    isVideoFile(file) {
      if (!file) return false;
      if (file.raw && file.raw.type) {
        return file.raw.type.startsWith('video/');
      }
      if (file.type) {
        return file.type.startsWith('video/');
      }
      return !!(file.url && file.url.match(/\.(mp4|mov|webm|ogg)$/i));
    },
    handleBeforeUpload(file) {
      const isVideo = file.type.indexOf('video') !== -1;
      const isImage = file.type.indexOf('image') !== -1;

      // --- 新增大小限制逻辑 ---
      const imageLimit = 10; // 图片限制 10MB
      const videoLimit = 500; // 视频限制 500MB

      if (isImage) {
        const isLt10M = file.size / 1024 / 1024 < imageLimit;
        if (!isLt10M) {
          this.$message.warning(`上传图片大小不能超过 ${imageLimit}MB!`);
          return false;
        }
      }

      if (isVideo) {
        const isLt100M = file.size / 1024 / 1024 < videoLimit;
        if (!isLt100M) {
          this.$message.warning(`上传视频大小不能超过 ${videoLimit}MB!`);
          return false;
        }
      }
      // -----------------------

      // before-upload 时，当前选择文件已进入 uploadFiles，故使用 > 做上限判断
      const { videoCount, imageCount } = this.getMediaCount();
      if (isVideo) {
        if (videoCount > 1) {
          this.$message.warning('最多可上传1个视频');
          return false;
        }
      }

      if (isImage) {
        if (imageCount > 5) {
          this.$message.warning('最多可上传5张图片');
          return false;
        }
      }
      return true;
    },
    handleExceed(files) {
      const firstFile = files && files[0];
      const isVideo = !!(firstFile && firstFile.type && firstFile.type.indexOf('video') !== -1);
      const isImage = !!(firstFile && firstFile.type && firstFile.type.indexOf('image') !== -1);
      const { videoCount, imageCount } = this.getMediaCount();

      if (isVideo && videoCount >= 1) {
        this.$message.warning('最多可上传1个视频');
        return;
      }
      if (isImage && imageCount >= 5) {
        this.$message.warning('最多可上传5张图片');
        return;
      }
      this.$message.warning('最多可上传1个视频和5张图片');
    },
    async customUpload(options) {
      const { file, onSuccess, onError } = options;
      try {
        // 上传
        const ext = file.name.split('.').pop();
        const url = await uploadFile(file, ext);

        //在 fileList 中找到当前正在上传的文件对象并更新它
        const targetFile = this.fileList.find(item => item.uid === file.uid);
        if (targetFile) {
          this.$set(targetFile, 'url', url);
          // 同时存入 response 结构，兼容旧的 submit 逻辑
          this.$set(targetFile, 'response', { data: { key: url } });
        }

        onSuccess({ data: { key: url } }, file);
      } catch (e) {
        console.error('上传失败:', e);
        this.$message.error('上传失败');
        onError(e);
      }
    },
    async getEncryptedPhone(phone) {
      try {
        const res = await getPublicKey();
        // 容错处理：根据实际接口返回结构调整
        const publicKey = res.data;

        if (!publicKey) throw new Error('Public key not found');

        const encryptor = new JSEncrypt();
        encryptor.setPublicKey(publicKey);
        const encrypted = encryptor.encrypt(phone);
        return encrypted || '';
      } catch (e) {
        console.error('RSA Encryption Error:', e);
        return '';
      }
    },

    async submitForm(formName) {
      this.$refs[formName].validate(async (valid) => {
        if (!valid) return;

        this.submitting = true;
        const imgList = [];
        const videoList = [];

        this.fileList.forEach(file => {
          const url = (file.response && file.response.data) ? file.response.data.key : (file.url || '');
          if (url) {
            if (this.isVideoFile(file)) {
              videoList.push(url);
            } else {
              imgList.push(url);
            }
          }
        });

        const encryptedPhone = await this.getEncryptedPhone(this.ruleForm.phone);
        if (!encryptedPhone) {
          this.$message.error('安全加密失败，请刷新重试');
          this.submitting = false;
          return;
        }

        const postData = {
          name: this.ruleForm.name,
          phone: encryptedPhone,
          organization: this.providerType === '单位' ? this.ruleForm.organization : '个人提供',
          content: this.ruleForm.content,
          videoUrl: videoList[0] || '',
          images: imgList.join(',')
        };

        reportInfoSave(postData).then(res => {
          this.$message.success('提交成功');
          this.resetForm(formName);
        }).finally(() => {
          this.submitting = false;
        });
      });
    },

    resetForm(formName) {
      this.$refs[formName].resetFields();
      this.fileList = [];
      this.providerType = '个人';
    },
    onDelete(file) {
      // 使用组件内置移除，确保重复文件时只删当前 uid
      if (this.$refs.upload) {
        this.$refs.upload.handleRemove(file);
      }
    },
    handleRemove(file, fileList) {
      // on-remove 回调以组件返回的 fileList 为准，避免重复文件互相影响
      if (Array.isArray(fileList)) {
        this.fileList = fileList.slice();
      } else {
        this.fileList = this.fileList.filter(item => item.uid !== file.uid);
      }
    },
    handlePreview(file) {
      this.dialogImageUrl = file.url;
      this.previewType = this.isVideoFile(file) ? 'video' : 'image';
      this.dialogVisible = true;
    },
  },
  computed: {
    isUploadLimit() {
      const hasVideo = this.fileList.some(item => this.isVideoFile(item));
      const imageCount = this.fileList.filter(item => !this.isVideoFile(item)).length;
      return this.fileList.length >= 6 || (hasVideo && imageCount >= 5);
    }
  }
};
</script>

<style lang="less" scoped>
.container {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 30px 0;

  .header-title {
    text-align: center;
    font-size: 30px;
    font-weight: bold;
    color: #333;
    margin-bottom: 30px;
    letter-spacing: 1px;
  }

  .form-wrapper {
    margin: 0 auto;
    width: 860px;
    background: #fff;
    padding: 45px 50px;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.05);

    /deep/ .el-form-item__label {
      font-size: 16px;
      padding-right: 30px;
      color: #333;
    }

    /* 证据上传标题两行显示样式微调 */
    /deep/ .upload-item-block {
      .el-form-item__label {
        line-height: 1.3;    /* 紧凑行高 */
        padding-top: 5px;    /* 垂直对齐微调 */
      }
    }
  }

  /* 居中按钮容器：脱离 Form Item 的 label 偏移 */
  .submit-btn-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 40px;

    .publish-btn {
      width: 280px;
      height: 54px;
      font-size: 18px;
      border-radius: 27px;
      background-color: #e64340;
      border: none;
      box-shadow: 0 4px 12px rgba(230, 67, 64, 0.3);

      &:hover { background-color: #f0625f; }
    }
  }

  /deep/ .hide-upload .el-upload--picture-card {
    display: none; /* 达到限制后隐藏那个加号方框 */
  }
}

@media screen and (max-width: 768px) {
  .container {
    .form-wrapper {
      width: 95%;
      padding: 25px 15px;
      /deep/ .el-form-item__label { width: 100% !important; text-align: left; padding-bottom: 5px; }
      /deep/ .el-form-item__content { margin-left: 0 !important; }
      .submit-btn-wrapper .publish-btn { width: 100%; }
    }
  }
}
</style>
